import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Ban, CalendarDays, CalendarPlus, Clock, Globe, Info } from 'lucide-react';

// Resumen de horarios (oct-2026): todo lo que decide cuándo hay turnos, en
// un solo lugar y sin poder cambiarlo. Lo ve la profesional en Horarios y la
// secretaria desde su panel. Nació de "uno a veces se olvida": un horario
// propio de un servicio que dejaba solo los lunes, bloqueos de la agenda que
// no aparecían en Horarios y días cerrados en Horarios que la agenda no
// mostraba. Los datos salen de GET /api/schedule/professional/:id/resumen.

type Dia = { dia: number; atiende: boolean; desde: string | null; hasta: string | null; corteDesde: string | null; corteHasta: string | null };
type Sede = { id: number | null; nombre: string | null; principal: boolean; soloEspeciales: boolean; semana: Dia[] };
type Franja = { dia: number; desde: string; hasta: string; abreNoLaboral: boolean; sedeId: number | null };
type ServicioPropio = { id: number; nombre: string; duracion: number; franjas: Franja[] };
type Especial = { id: number; fecha: string; desde: string; hasta: string; corteDesde: string | null; corteHasta: string | null; motivo: string | null; sedeId: number | null };
type Bloqueo = { id: string; fecha: string; tipo: 'dia' | 'rango' | 'resto_del_dia'; tipoCierre: string | null; desde: string | null; hasta: string | null; motivo: string | null; sedeId: number | null; origen: 'horarios' | 'agenda' };
type Aviso = { nivel: 'alerta' | 'info'; codigo: string; texto: string };
type Resumen = {
  reservas: { hastaDias: number; hastaFecha: string; antelacionHoras: number; soloEspeciales: boolean };
  avisos?: Aviso[];
  sedes: Sede[];
  serviciosPropios: ServicioPropio[];
  especiales: Especial[];
  bloqueos: Bloqueo[];
};

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const ORDEN = [1, 2, 3, 4, 5, 6, 0];
const DIAS = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
const DIAS_PLURAL = ['domingos', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábados'];
const DIAS_CORTOS = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];
const TIPOS_DE_CIERRE: Record<string, string> = { holiday: 'feriado', vacation: 'vacaciones', personal: 'motivos personales', sick: 'enfermedad' };

const fechaCorta = (iso: string) => {
  const [a, m, d] = iso.split('-').map(Number);
  return `${DIAS_CORTOS[new Date(a, m - 1, d).getDay()]} ${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}`;
};
const listar = (items: string[]) => (items.length <= 1 ? items[0] || '' : `${items.slice(0, -1).join(', ')} y ${items[items.length - 1]}`);
// "Horario bloqueado" es el motivo que se pone solo cuando no escriben ninguno
const notaReal = (motivo: string | null, repetida?: string) => {
  const t = (motivo || '').trim();
  if (!t || /^horarios? bloqueados?$/i.test(t)) return null;
  if (repetida && t.toLowerCase() === repetida) return null;
  return t;
};

const Seccion: React.FC<{ icono: React.ReactNode; tono: string; titulo: string; pie?: string; children: React.ReactNode }> = ({ icono, tono, titulo, pie, children }) => (
  <section className="bg-white rounded-3xl border border-gray-200 shadow-sm p-4 sm:p-5 text-left">
    <div className="flex items-center gap-2.5 mb-3">
      <span className={`w-8 h-8 rounded-xl flex items-center justify-center ${tono}`}>{icono}</span>
      <h3 className="text-base font-bold text-gray-900">{titulo}</h3>
    </div>
    {children}
    {pie && <p className="mt-3 text-xs text-gray-500">{pie}</p>}
  </section>
);

const ResumenHorarios: React.FC<{ professionalId: number | string }> = ({ professionalId }) => {
  const [datos, setDatos] = useState<Resumen | null>(null);
  const [error, setError] = useState(false);
  // "Para revisar" muestra dos y el resto a pedido: con varios feriados
  // abiertos la tarjeta tapaba todo el resumen (pedido de Esteban, oct-2026)
  const [todosLosAvisos, setTodosLosAvisos] = useState(false);

  const cargar = useCallback(async () => {
    setError(false);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/schedule/professional/${professionalId}/resumen`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined
      });
      const d = r.ok ? await r.json() : null;
      if (!d?.success) throw new Error('sin resumen');
      setDatos(d);
    } catch {
      setError(true);
    }
  }, [professionalId]);

  useEffect(() => { setDatos(null); cargar(); }, [cargar]);

  if (error) {
    return (
      <div className="bg-white rounded-3xl border border-gray-200 p-5 text-left text-sm text-gray-600">
        No se pudo cargar el resumen.{' '}
        <button type="button" onClick={cargar} className="font-semibold text-blue-600 hover:underline">Probar de nuevo</button>
      </div>
    );
  }
  if (!datos) {
    return (
      <div className="space-y-3" aria-busy="true">
        {[0, 1, 2].map((i) => <div key={i} className="h-28 rounded-3xl bg-white border border-gray-200 animate-pulse" />)}
      </div>
    );
  }

  const variasSedes = datos.sedes.length > 1;
  const nombreSede = (id: number | null) => (id && variasSedes ? datos.sedes.find((s) => s.id === id)?.nombre || null : null);

  // Franjas de servicios con horario propio que valen en esta sede ese día
  const franjasDelDia = (sedeId: number | null, dia: number) =>
    datos.serviciosPropios.flatMap((s) => s.franjas
      .filter((f) => f.dia === dia && (f.sedeId == null || f.sedeId === sedeId))
      .map((f) => ({ servicio: s, franja: f })));

  const textoBloqueo = (b: Bloqueo) => {
    if (b.tipo === 'dia') {
      const tipo = b.tipoCierre ? TIPOS_DE_CIERRE[b.tipoCierre] : '';
      return `Día cerrado${tipo ? ` por ${tipo}` : ''}`;
    }
    if (b.tipo === 'rango') return `Bloqueado de ${b.desde} a ${b.hasta}`;
    return `Bloqueado desde las ${b.desde}`;
  };

  // Dónde se arregla cada aviso, según quién lo mira
  const pista = (codigo: string) => {
    if (codigo.startsWith('servicio_')) return 'Se cambia en Servicios.';
    if (codigo === 'feriado_abierto') return 'Se bloquea desde la agenda, con "Bloquear" ese día.';
    if (codigo === 'especial_tapado') return 'Se saca con "Desbloquear" en la agenda, ese día.';
    return '';
  };
  const avisos = datos.avisos || [];
  const AVISOS_A_LA_VISTA = 2;
  const avisosVisibles = todosLosAvisos ? avisos : avisos.slice(0, AVISOS_A_LA_VISTA);
  const avisosOcultos = avisos.length - AVISOS_A_LA_VISTA;

  return (
    <div className="space-y-4">
      {/* Lo que conviene revisar, arriba de todo */}
      {avisos.length > 0 && (
        <section className="rounded-3xl border border-amber-200 bg-amber-50 p-4 sm:p-5 text-left">
          <div className="flex items-center gap-2.5 mb-3">
            <span className="w-8 h-8 rounded-xl flex items-center justify-center bg-amber-100">
              <AlertTriangle className="w-4 h-4 text-amber-700" />
            </span>
            <h3 className="text-base font-bold text-gray-900">Para revisar</h3>
            {avisos.length > AVISOS_A_LA_VISTA && (
              <span className="rounded-full bg-amber-100 text-amber-800 text-xs font-semibold px-2 py-0.5">{avisos.length}</span>
            )}
          </div>
          <ul className="space-y-2.5">
            {avisosVisibles.map((a, i) => (
              <li key={i} className="flex items-start gap-2">
                {a.nivel === 'alerta'
                  ? <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" />
                  : <Info className="w-4 h-4 mt-0.5 shrink-0 text-sky-600" />}
                <p className="text-sm text-gray-800">
                  {a.texto}
                  {pista(a.codigo) && <span className="block text-xs text-gray-500 mt-0.5">{pista(a.codigo)}</span>}
                </p>
              </li>
            ))}
          </ul>
          {avisosOcultos > 0 && (
            <button
              type="button"
              onClick={() => setTodosLosAvisos(!todosLosAvisos)}
              className="mt-3 rounded-xl border border-amber-300 bg-white px-3 py-1.5 text-sm font-semibold text-amber-800 hover:bg-amber-100 transition-colors"
              aria-expanded={todosLosAvisos}
            >
              {todosLosAvisos ? 'Ver menos' : `Ver ${avisosOcultos} más`}
            </button>
          )}
        </section>
      )}

      {/* Hasta cuándo se reserva */}
      <Seccion icono={<Globe className="w-4 h-4 text-violet-700" />} tono="bg-violet-100" titulo="Reservas online"
        pie="Se cambia en la solapa Semana → Reservas online.">
        <p className="text-sm text-gray-800">
          Se puede reservar hasta el <strong>{fechaCorta(datos.reservas.hastaFecha)}</strong>
          {' '}({datos.reservas.hastaDias} días adelante)
          {datos.reservas.antelacionHoras > 0 && <>, con al menos <strong>{datos.reservas.antelacionHoras} h</strong> de anticipación</>}.
        </p>
        {datos.reservas.soloEspeciales && (
          <p className="mt-1.5 text-sm text-amber-800">Solo se atiende en días especiales: la semana de abajo no se usa.</p>
        )}
      </Seccion>

      {/* La semana de cada sede, con los servicios de horario propio encima */}
      {datos.sedes.map((sede) => (
        <Seccion key={String(sede.id)} icono={<CalendarDays className="w-4 h-4 text-blue-700" />} tono="bg-blue-100"
          titulo={variasSedes ? `${sede.nombre}${sede.principal ? ' (principal)' : ''}` : 'Semana'}
          pie="La semana se cambia en la solapa Semana; los horarios propios, en Servicios.">
          {sede.soloEspeciales && !datos.reservas.soloEspeciales && (
            <p className="mb-2 text-sm text-amber-800">Esta sede atiende solo en días especiales.</p>
          )}
          <ul className="divide-y divide-gray-100">
            {ORDEN.map((n) => {
              const d = sede.semana.find((x) => x.dia === n);
              const extras = franjasDelDia(sede.id, n);
              return (
                <li key={n} className="py-2 flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-3">
                  <span className={`w-24 shrink-0 text-sm font-semibold ${d?.atiende ? 'text-gray-900' : 'text-gray-400'}`}>{DIAS[n]}</span>
                  <div className="flex-1 min-w-0">
                    {d?.atiende ? (
                      <p className="text-sm text-gray-800">
                        {d.desde} a {d.hasta}
                        {d.corteDesde && d.corteHasta && <span className="text-gray-500"> · corte de {d.corteDesde} a {d.corteHasta}</span>}
                      </p>
                    ) : (
                      <p className="text-sm text-gray-400">No se atiende</p>
                    )}
                    {extras.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        {extras.map(({ servicio, franja }, i) => {
                          const abre = !d?.atiende && franja.abreNoLaboral;
                          const soloEspeciales = !d?.atiende && !franja.abreNoLaboral;
                          const tono = abre ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                            : soloEspeciales ? 'bg-gray-50 text-gray-600 border-gray-200'
                              : 'bg-sky-50 text-sky-800 border-sky-200';
                          return (
                            <span key={i} className={`inline-flex items-center gap-1 rounded-lg border px-2 py-0.5 text-xs ${tono}`}>
                              <Clock className="w-3 h-3 shrink-0" />
                              <span><strong className="font-semibold">{servicio.nombre}</strong>: {d?.atiende ? 'solo ' : ''}{franja.desde} a {franja.hasta}
                                {abre && ' · abre el día aunque no se atienda'}
                                {soloEspeciales && ' · solo en días especiales'}</span>
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </Seccion>
      ))}

      {/* Servicios que no siguen la semana */}
      {datos.serviciosPropios.length > 0 && (
        <Seccion icono={<Clock className="w-4 h-4 text-sky-700" />} tono="bg-sky-100" titulo="Servicios con horario propio"
          pie="Los demás servicios siguen la semana de arriba. Se cambia en Servicios.">
          <ul className="space-y-2">
            {datos.serviciosPropios.map((s) => {
              const porDia = ORDEN.filter((n) => s.franjas.some((f) => f.dia === n)).map((n) => {
                const fr = s.franjas.filter((f) => f.dia === n);
                const sedes = Array.from(new Set(fr.map((f) => nombreSede(f.sedeId)).filter(Boolean)));
                return `los ${DIAS_PLURAL[n]} ${listar(fr.map((f) => `de ${f.desde} a ${f.hasta}`))}${sedes.length ? ` (${sedes.join(', ')})` : ''}`;
              });
              return (
                <li key={s.id} className="rounded-2xl bg-sky-50/60 border border-sky-100 px-3 py-2">
                  <p className="text-sm font-semibold text-gray-900">{s.nombre} <span className="font-normal text-gray-500">· {s.duracion} min</span></p>
                  <p className="text-sm text-gray-700">Solo {listar(porDia)}.</p>
                </li>
              );
            })}
          </ul>
        </Seccion>
      )}

      {/* Días especiales que vienen */}
      <Seccion icono={<CalendarPlus className="w-4 h-4 text-emerald-700" />} tono="bg-emerald-100" titulo="Días especiales que vienen"
        pie={'Se cargan con "Abrir día" en la agenda o en la solapa Especiales.'}>
        {datos.especiales.length === 0 ? (
          <p className="text-sm text-gray-500">No hay días especiales cargados.</p>
        ) : (
          <ul className="space-y-1.5">
            {datos.especiales.map((e) => (
              <li key={e.id} className="flex items-start gap-3">
                <span className="w-20 shrink-0 text-sm font-semibold text-emerald-800">{fechaCorta(e.fecha)}</span>
                <span className="text-sm text-gray-800">
                  {e.desde} a {e.hasta}
                  {e.corteDesde && e.corteHasta && <span className="text-gray-500"> · corte de {e.corteDesde} a {e.corteHasta}</span>}
                  {nombreSede(e.sedeId) && <span className="text-gray-500"> · {nombreSede(e.sedeId)}</span>}
                  {notaReal(e.motivo, 'día especial') && <span className="text-gray-500"> · {notaReal(e.motivo, 'día especial')}</span>}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Seccion>

      {/* Todos los bloqueos, vengan de Horarios o de la agenda */}
      <Seccion icono={<Ban className="w-4 h-4 text-red-700" />} tono="bg-red-100" titulo="Bloqueos que vienen"
        pie={'Los de la agenda se sacan con "Desbloquear" en ese día; los de Horarios, en la solapa Bloqueos.'}>
        {datos.bloqueos.length === 0 ? (
          <p className="text-sm text-gray-500">No hay bloqueos cargados.</p>
        ) : (
          <ul className="space-y-1.5">
            {datos.bloqueos.map((b) => {
              const tipo = b.tipoCierre ? TIPOS_DE_CIERRE[b.tipoCierre] : undefined;
              const nota = notaReal(b.motivo, tipo);
              const sede = b.tipo === 'rango' ? (variasSedes ? 'todas las sedes' : null) : nombreSede(b.sedeId);
              return (
                <li key={b.id} className="flex items-start gap-3">
                  <span className="w-20 shrink-0 text-sm font-semibold text-red-800">{fechaCorta(b.fecha)}</span>
                  <span className="flex-1 text-sm text-gray-800">
                    {textoBloqueo(b)}
                    {sede && <span className="text-gray-500"> · {sede}</span>}
                    {nota && <span className="text-gray-500"> · {nota}</span>}
                  </span>
                  <span className={`shrink-0 rounded-md px-1.5 py-0.5 text-[11px] font-medium ${b.origen === 'agenda' ? 'bg-indigo-50 text-indigo-700' : 'bg-gray-100 text-gray-600'}`}>
                    {b.origen === 'agenda' ? 'Agenda' : 'Horarios'}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </Seccion>
    </div>
  );
};

export default ResumenHorarios;
